import React, { useState } from 'react';
import { PageSection, SiteData, SitePage, HeaderMenuItem, HeaderMenuTargetType } from '../../../types';
import { Phone, Menu as MenuIcon, X, Globe, ChevronRight, Sliders, ChevronDown, ExternalLink } from 'lucide-react';
import { WebsiteButton } from '../../common/WebsiteButton';
import { isDarkBackground, getOptimalTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  activePage?: SitePage;
  isPreview?: boolean;
  onNavigate?: (slug: string) => void;
  onOpenHeaderSettings?: () => void;
}

export const HeaderSection: React.FC<SectionProps> = ({
  section,
  siteData,
  activePage,
  isPreview = false,
  onNavigate,
  onOpenHeaderSettings,
}) => {
  const content = section.content || {};
  const style = section.style || {};

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const brandName = siteData.settings?.logoText || siteData.settings?.businessName || siteData.name || 'Adiosoft';
  const logoUrl = siteData.settings?.logoUrl;
  const logoWidth = siteData.settings?.logoWidth || 140;
  const phone = siteData.settings?.phone || '';
  
  const buttonText = siteData.settings?.headerButtonText || content.buttonText || (siteData.industry === 'YACHT_RENTAL' ? 'Rezervasyon Yap' : 'Teklif Al');
  const buttonAction = siteData.settings?.headerButtonAction || {
    type: 'page' as const,
    targetPage: content.buttonLink || (siteData.industry === 'YACHT_RENTAL' ? '/rezervasyon' : '/iletisim'),
  };

  const isTransparent = Boolean(siteData.settings?.headerTransparent);
  const bgColor = isTransparent ? 'transparent' : (style.backgroundColor || siteData.settings?.backgroundColor || '#FFFFFF');
  const isDarkBg = isDarkBackground(bgColor === 'transparent' ? '#0F172A' : bgColor);
  const textColor = style.textColor || (isDarkBg ? '#FFFFFF' : '#0F172A');
  const navLinkColor = isDarkBg ? 'text-slate-200 hover:text-white' : 'text-slate-700 hover:text-slate-950';
  const accentColor = siteData.settings?.accentColor || '#2563EB';

  // Dynamic menu items: read from settings.headerMenu or generate from pages
  const menuItems: HeaderMenuItem[] =
    siteData.settings?.headerMenu && siteData.settings.headerMenu.length > 0
      ? siteData.settings.headerMenu
      : (siteData.pages || []).map((p, idx) => ({
          id: p.id || `menu-${idx}`,
          label: p.title,
          targetType: 'page',
          type: 'page',
          targetId: p.slug,
          target: p.slug,
          order: idx,
        }));

  const handleMenuItemClick = (item: HeaderMenuItem) => {
    setMobileMenuOpen(false);
    const targetType: HeaderMenuTargetType = item.targetType || (item.type as HeaderMenuTargetType) || 'page';
    const rawTarget = item.targetId || item.target || item.url || '';

    switch (targetType) {
      case 'page': {
        const slug = rawTarget || '/';
        if (onNavigate) onNavigate(slug.startsWith('/') ? slug : `/${slug}`);
        break;
      }
      case 'section': {
        const secId = rawTarget.replace(/^#/, '');
        const el = document.getElementById(secId) || document.querySelector(`[data-section-id="${secId}"]`) || document.querySelector(`#${secId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (onNavigate) {
          // If not found on current page, navigate to home and append hash
          onNavigate(`/#${secId}`);
        }
        break;
      }
      case 'external':
      case 'custom_url' as any: {
        if (rawTarget) {
          const url = rawTarget.startsWith('http') ? rawTarget : `https://${rawTarget}`;
          if (item.openInNewTab !== false) {
            window.open(url, '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = url;
          }
        }
        break;
      }
      case 'phone': {
        if (rawTarget) window.location.href = `tel:${rawTarget.replace(/[^0-9+]/g, '')}`;
        break;
      }
      case 'whatsapp': {
        if (rawTarget) {
          const cleanPhone = rawTarget.replace(/[^0-9]/g, '');
          window.open(`https://wa.me/${cleanPhone}`, '_blank');
        }
        break;
      }
      case 'email': {
        if (rawTarget) window.location.href = `mailto:${rawTarget}`;
        break;
      }
      default: {
        if (onNavigate) onNavigate(rawTarget);
      }
    }
  };

  return (
    <header
      className={`top-0 z-40 w-full transition-all border-b select-none ${
        siteData.settings?.stickyHeader !== false ? 'sticky' : 'relative'
      } ${isTransparent ? 'backdrop-blur-md bg-opacity-90' : ''}`}
      style={{
        backgroundColor: bgColor,
        color: textColor,
        borderColor: isDarkBg ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Brand / Logo */}
        <div
          onClick={() => onNavigate && onNavigate('/')}
          className="flex items-center gap-3 cursor-pointer group"
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={brandName}
              style={{ width: `${logoWidth}px` }}
              className="h-10 object-contain"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div
              className="w-10 h-10 rounded-2xl flex items-center justify-center font-black text-white text-base shadow-lg group-hover:scale-105 transition-transform"
              style={{ backgroundColor: accentColor }}
            >
              {brandName.charAt(0)}
            </div>
          )}
          <div className="flex flex-col">
            <span
              className={`font-black text-lg sm:text-xl tracking-tight leading-none ${
                isDarkBg ? 'text-white' : 'text-slate-900'
              }`}
            >
              {brandName}
            </span>
            {siteData.settings?.siteName && (
              <span className="text-[10px] text-slate-500 font-semibold tracking-wide uppercase mt-1">
                {siteData.settings.siteName}
              </span>
            )}
          </div>
        </div>

        {/* Desktop Navigation Links */}
        <nav className="hidden md:flex items-center gap-7">
          {menuItems.map((item) => {
            const effectiveTargetType = item.targetType || (item.type as HeaderMenuTargetType) || 'page';
            const effectiveTarget = item.targetId || item.target || '';
            const isPageActive = effectiveTargetType === 'page' && (activePage?.slug === effectiveTarget || (effectiveTarget === '/' && activePage?.isHomePage));

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleMenuItemClick(item)}
                className={`text-xs font-bold uppercase tracking-wider transition-colors relative py-1 flex items-center gap-1 ${navLinkColor} ${
                  isPageActive ? 'font-extrabold' : ''
                }`}
                style={{
                  color: isPageActive ? accentColor : undefined,
                }}
              >
                <span>{item.label}</span>
                {effectiveTargetType === 'external' && <ExternalLink className="w-2.5 h-2.5 opacity-60" />}
                {isPageActive && (
                  <span
                    className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                    style={{ backgroundColor: accentColor }}
                  />
                )}
              </button>
            );
          })}
        </nav>

        {/* Action Button & Contact */}
        <div className="hidden lg:flex items-center gap-4">
          {phone && (
            <a
              href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
              className={`flex items-center gap-2 text-xs font-bold transition px-3 py-1.5 rounded-xl ${
                isDarkBg ? 'text-slate-300 hover:text-white hover:bg-white/5' : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'
              }`}
            >
              <Phone className="w-3.5 h-3.5" style={{ color: accentColor }} />
              <span>{phone}</span>
            </a>
          )}

          {buttonText && (
            <WebsiteButton
              label={buttonText}
              action={buttonAction}
              accentColor={accentColor}
              size="sm"
              onNavigate={onNavigate}
              isEditor={!isPreview}
            />
          )}

          {!isPreview && onOpenHeaderSettings && (
            <button
              type="button"
              onClick={onOpenHeaderSettings}
              title="Header Ayarlarını Düzenle"
              className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
            >
              <Sliders className="w-4 h-4 text-blue-400" />
            </button>
          )}
        </div>

        {/* Mobile Hamburger Toggle */}
        <div className="flex md:hidden items-center gap-2">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`p-2 rounded-xl border ${
              isDarkBg ? 'bg-white/10 text-white border-white/10' : 'bg-slate-100 text-slate-900 border-slate-200'
            }`}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div
          className={`md:hidden border-t p-6 space-y-4 animate-in slide-in-from-top-2 ${
            isDarkBg ? 'bg-slate-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900 shadow-2xl'
          }`}
        >
          <nav className="flex flex-col space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleMenuItemClick(item)}
                className={`text-left py-2.5 font-bold text-sm flex items-center justify-between border-b ${
                  isDarkBg ? 'border-white/5 text-slate-200 hover:text-white' : 'border-slate-100 text-slate-800 hover:text-blue-600'
                }`}
              >
                <span>{item.label}</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </nav>

          {phone && (
            <a
              href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold border ${
                isDarkBg ? 'border-white/10 text-slate-300' : 'border-slate-200 text-slate-700 bg-slate-50'
              }`}
            >
              <Phone className="w-4 h-4" style={{ color: accentColor }} />
              <span>{phone}</span>
            </a>
          )}

          {buttonText && (
            <div className="w-full">
              <WebsiteButton
                label={buttonText}
                action={buttonAction}
                accentColor={accentColor}
                size="md"
                className="w-full text-center"
                onNavigate={(path) => {
                  setMobileMenuOpen(false);
                  if (onNavigate) onNavigate(path);
                }}
              />
            </div>
          )}
        </div>
      )}
    </header>
  );
};

